import { useId, useState, type ReactNode } from "react";

import { Plus } from "@/components/ui/icons";
import { cn } from "@/utils/cn";

// Figma: https://www.figma.com/design/HJIp5P6tyeFrUd7ODqeWdM/-PROD--Fertility-V?node-id=4180-18739
// (Core DS `accordion`). The dashed variant is the FAQ list, node 4180-18930. Sizes follow
// evvy.com/fertility-eap, which the Fertility-V page copies.

export const ACCORDION_VARIANTS = ["solid", "dashed"] as const;

export type AccordionVariant = (typeof ACCORDION_VARIANTS)[number];

type AccordionProps = {
  title: ReactNode;
  /** Shown when open. */
  children: ReactNode;
  variant?: AccordionVariant;
  defaultOpen?: boolean;
  /** Layout only. A repeated look is a variant. */
  className?: string;
};

// The 15px and 16px row padding and 12px FAQ inset are marketing's, with no Figma variable (EVY-436).
const VARIANTS: Record<AccordionVariant, string> = {
  solid: "border-b border-ds-line-light-on-white py-[15px]",
  dashed: "border-b border-dashed border-ds-line-black px-[12px] py-[16px]",
};

// Answers are 14px on 21px; markdown lists and paragraphs keep that size and a 14px rhythm.
const ANSWER =
  "ds-body-small-regular [&_li]:[font-size:inherit] [&_li]:[line-height:inherit] [&_p]:mb-[14px] [&_p]:mt-0";

const ANSWERS: Record<AccordionVariant, string> = {
  solid: cn(ANSWER, "text-ds-text-light-on-white"),
  dashed: cn(ANSWER, "text-ds-text-primary/80"),
};

// evvy.com's motion: 0.5s on an expo in-out curve, none when the viewer asks for reduced motion.
const MOTION =
  "duration-500 ease-[cubic-bezier(.77,0,.175,1)] motion-reduce:transition-none";

/** Classes for a panel that grows open: its grid row goes from 0fr to 1fr and it fades in. Closed,
 * it's invisible, so its links leave the tab order. Wrap the content in one `overflow-hidden` div. */
export const expandablePanelClasses = (open: boolean): string =>
  cn(
    "grid transition-[grid-template-rows,opacity,visibility]",
    MOTION,
    open
      ? "visible grid-rows-[1fr] opacity-100"
      : "invisible grid-rows-[0fr] opacity-0"
  );

// No ref forwarded. See ui/README.md.
export const Accordion = ({
  title,
  children,
  variant = "solid",
  defaultOpen = false,
  className,
}: AccordionProps) => {
  const [open, setOpen] = useState(defaultOpen);
  const panelId = useId();
  return (
    <div className={cn("text-ds-text-primary", VARIANTS[variant], className)}>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen(!open)}
        className="unset flex w-full cursor-pointer items-center justify-between gap-ds-space-06 text-left"
      >
        <span className="ds-body-base-medium">{title}</span>
        {/* The plus turns 225 degrees into the close mark. */}
        <Plus
          className={cn(
            "shrink-0 transition-transform",
            MOTION,
            open && "rotate-[225deg]"
          )}
        />
      </button>
      <div id={panelId} className={expandablePanelClasses(open)}>
        <div className="overflow-hidden">
          <div className={cn("pt-ds-space-04", ANSWERS[variant])}>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};
